import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  AdminPaymentGatewayCatalog,
  AdminUpdatePaymentGatewayRequest,
} from "@ternary/contracts"
import { Badge, Button, Checkbox, Field, Input, Select, Tabs, type WizardErrors } from "@ternary/ui"
import { type FormEvent, useId, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { Link, useSearchParams } from "react-router-dom"
import {
  connectAdminPaymentGateway,
  loadAdminPaymentGateway,
  updateAdminPaymentGateway,
} from "../api.js"
import { FormLoading } from "../components/form-loading.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { useErrorsUntilEdited } from "../components/setup-page.js"
import { ConnectionsHeader } from "./admin-discord-page.js"

type PaymentTab = "tax" | "checkout"

/**
 * The tax, referral coupon and checkout settings, in a Tax and a Checkout tab so neither scrolls
 * with a new tax rate's fields open. Its Save is in the page's Save bar; a new tax rate that does
 * not check out opens the Tax tab with the first invalid field focused.
 */
function PaymentSettingsForm({
  catalog,
  id,
  onSave,
  onTabChange,
  tab,
}: {
  catalog: AdminPaymentGatewayCatalog
  id: string
  onSave(settings: AdminUpdatePaymentGatewayRequest): void
  onTabChange(tab: PaymentTab): void
  tab: PaymentTab
}) {
  const formRef = useRef<HTMLFormElement>(null)
  const [taxRateId, setTaxRateId] = useState(catalog.selectedTaxRateId ?? "")
  const [taxDisplayName, setTaxDisplayName] = useState("")
  const [taxPercentage, setTaxPercentage] = useState("")
  const [taxInclusive, setTaxInclusive] = useState(false)
  const [referralCouponId, setReferralCouponId] = useState(catalog.referralCouponId ?? "")
  const [requireBillingAddress, setRequireBillingAddress] = useState(catalog.requireBillingAddress)
  const [requireLoginForPurchase, setRequireLoginForPurchase] = useState(
    catalog.requireLoginForPurchase,
  )
  const [errors, setErrors] = useState<WizardErrors>({})
  const shown = useErrorsUntilEdited(errors, { taxDisplayName, taxPercentage })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const found: WizardErrors = {}
    if (taxRateId === "new") {
      const percentage = Number(taxPercentage)
      if (!taxPercentage.trim() || !(percentage >= 0.01 && percentage <= 100)) {
        found.taxPercentage = "Enter a percentage from 0.01 to 100."
      } else if (Math.round(percentage * 100) / 100 !== percentage) {
        // The form is noValidate, so the field's native step="0.01" no longer enforces this.
        found.taxPercentage = "Use at most two decimal places, such as 5.55."
      }
      if (!taxDisplayName.trim()) {
        found.taxDisplayName = "Enter the name customers see, such as HST."
      }
    }
    const blocked = Object.keys(found).length > 0
    // Rendered now, with the Tax tab open, so the first invalid field can take focus.
    flushSync(() => {
      setErrors(found)
      if (blocked) onTabChange("tax")
    })
    if (blocked) {
      formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }
    onSave({
      newTaxRate:
        taxRateId === "new"
          ? {
              displayName: taxDisplayName,
              inclusive: taxInclusive,
              percentage: Number(taxPercentage),
            }
          : null,
      referralCouponId: referralCouponId || null,
      requireBillingAddress,
      requireLoginForPurchase,
      taxRateId: taxRateId || null,
    })
  }

  const checkboxes = (
    <>
      <Checkbox
        checked={requireBillingAddress}
        disabled={!catalog.writable}
        hint="Collect the customer's billing address during checkout."
        label="Require billing address"
        onChange={(event) => setRequireBillingAddress(event.target.checked)}
      />
      <Checkbox
        checked={requireLoginForPurchase}
        disabled={!catalog.writable}
        hint="Customers sign in before they start checkout."
        label="Require login for purchase"
        onChange={(event) => setRequireLoginForPurchase(event.target.checked)}
      />
    </>
  )

  const taxPanel = (
    <div className="admin-stack">
      <Field label="Tax">
        <Select
          disabled={!catalog.writable}
          onChange={(event) => setTaxRateId(event.target.value)}
          value={taxRateId}
        >
          <option value="">No tax</option>
          <option value="new">New tax rate</option>
          {catalog.taxRates.map((taxRate) => (
            <option key={taxRate.id} value={taxRate.id}>
              {taxRate.displayName} ({taxRate.percentage}%)
            </option>
          ))}
        </Select>
      </Field>
      {taxRateId === "new" ? (
        <>
          {/* Side by side even on a phone, so the tab fits its screen. */}
          <div className="admin-field-pair">
            <Field
              error={shown.taxPercentage}
              hint={shown.taxPercentage ? undefined : "Such as 13."}
              label="Tax rate"
              required
            >
              <Input
                inputMode="decimal"
                max="100"
                min="0.01"
                onChange={(event) => setTaxPercentage(event.target.value)}
                placeholder="13"
                size="short"
                step="0.01"
                suffix="%"
                type="number"
                value={taxPercentage}
              />
            </Field>
            <Field error={shown.taxDisplayName} label="Tax display name" required>
              <Input
                maxLength={100}
                onChange={(event) => setTaxDisplayName(event.target.value)}
                placeholder="HST"
                value={taxDisplayName}
              />
            </Field>
          </div>
          {/* Whether the rate is inclusive, in what it means for a price. The values sent stay. */}
          <Field label="Prices">
            <Select
              onChange={(event) => setTaxInclusive(event.target.value === "true")}
              value={String(taxInclusive)}
            >
              <option value="false">Tax added at checkout</option>
              <option value="true">Tax included in price</option>
            </Select>
          </Field>
        </>
      ) : null}
    </div>
  )

  const checkoutPanel = (
    <div className="admin-stack">
      <Field
        hint={
          <>
            Create coupons on the <Link to="/dashboard/coupons">Coupons</Link> page.
          </>
        }
        label="Coupon applied to referrals"
      >
        <Select
          disabled={!catalog.writable}
          onChange={(event) => setReferralCouponId(event.target.value)}
          value={referralCouponId}
        >
          <option value="">No discount</option>
          {catalog.coupons.map((coupon) => (
            <option key={coupon.id} value={coupon.id}>
              {coupon.name}
            </option>
          ))}
        </Select>
      </Field>
      {checkboxes}
    </div>
  )

  return (
    <form
      aria-label="Payment settings"
      className="admin-stack admin-payment-form"
      id={id}
      noValidate
      onSubmit={submit}
      ref={formRef}
    >
      {/* Only Stripe has tax and coupons; any other gateway has the checkout rules alone. */}
      {catalog.provider === "stripe" ? (
        <Tabs
          label="Payment settings"
          onChange={(next) => onTabChange(next as PaymentTab)}
          tabs={[
            { content: taxPanel, id: "tax", label: "Tax" },
            { content: checkoutPanel, id: "checkout", label: "Checkout" },
          ]}
          value={tab}
        />
      ) : (
        checkboxes
      )}
    </form>
  )
}

export function AdminPaymentGatewayPage() {
  const formId = useId()
  const queryClient = useQueryClient()
  // Here, outside the keyed form, so the open tab survives the reset that a save causes.
  const [tab, setTab] = useState<PaymentTab>("tax")
  const [searchParams] = useSearchParams()
  const [notice, setNotice] = useState(
    searchParams.get("connect") === "complete"
      ? "Stripe onboarding returned successfully. Connection status has been refreshed."
      : "",
  )
  const [actionError, setActionError] = useState("")
  const query = useQuery({
    queryFn: loadAdminPaymentGateway,
    queryKey: ["admin-payment-gateway"],
  })
  const update = useMutation({
    mutationFn: updateAdminPaymentGateway,
    onError(error) {
      setNotice("")
      setActionError(error instanceof Error ? error.message : "Payment settings could not update.")
    },
    onSuccess(data) {
      queryClient.setQueryData(["admin-payment-gateway"], data)
      setActionError("")
      setNotice("Successfully updated payment settings.")
    },
  })
  const connect = useMutation({
    mutationFn: connectAdminPaymentGateway,
    onError(error) {
      setNotice("")
      setActionError(error instanceof Error ? error.message : "Stripe Connect could not start.")
    },
    onSuccess(data) {
      const popup = window.open(data.url, "_blank", "noopener,noreferrer")
      setActionError("")
      setNotice(
        popup ? "Stripe onboarding opened in a new tab." : `Open Stripe onboarding: ${data.url}`,
      )
    },
  })

  if (query.isPending || query.isError) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <ConnectionsHeader active="stripe" />
        {query.isPending ? (
          <FormLoading fields={2} label="Loading payment gateways…" />
        ) : (
          <LoadError
            detail={errorDetail(query.error)}
            message="We couldn't load your payment gateways"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </section>
    )
  }

  const catalog = query.data
  const messages = (
    <>
      {actionError ? (
        <p className="admin-save-message tny-error" role="alert">
          {actionError}
        </p>
      ) : null}
      <p className="admin-save-message" role="status">
        {notice}
      </p>
    </>
  )
  return (
    <section className="admin-stack admin-page">
      <ConnectionsHeader active="stripe" />
      {/* The gateway and its status, from the catalog, with Connect Stripe until it is connected. */}
      {/* biome-ignore lint/a11y/useSemanticElements: a named group of a name, a status and an action, not a form's fieldset. */}
      <div aria-label="Payment gateway" className="admin-gateway" role="group">
        <span className="admin-gateway-name">Stripe</span>
        <Badge tone={catalog.connected ? "success" : "neutral"}>
          {catalog.connected ? "Connected" : "Not connected"}
        </Badge>
        {catalog.connected ? null : (
          <Button
            disabled={!catalog.connectAvailable}
            loading={connect.isPending}
            onClick={() => connect.mutate()}
            size="sm"
          >
            Connect Stripe
          </Button>
        )}
      </div>
      {!catalog.connected ? messages : null}
      {!catalog.connected ? (
        <p className="admin-note">
          Connect Stripe to sell products, collect subscriptions and manage taxes.
        </p>
      ) : (
        <>
          {/* Keyed by the saved settings, so a save or a refetch that changes them resets it. */}
          <PaymentSettingsForm
            catalog={catalog}
            id={formId}
            key={JSON.stringify(catalog)}
            onSave={(settings) => update.mutate(settings)}
            onTabChange={setTab}
            tab={tab}
          />
          {/* Outside the keyed form, so the messages survive the reset that a save causes. */}
          <div className="admin-save-bar">
            {messages}
            <Button
              disabled={!catalog.writable}
              form={formId}
              loading={update.isPending}
              type="submit"
            >
              Save
            </Button>
          </div>
        </>
      )}
    </section>
  )
}
